import type { Track } from '../types/domain';
import {
  isFixedProgramColor,
  type AnnualEffectiveCell,
  type AnnualProgramGrouping,
} from './annualTimetable';
import { getOrderedTrackNamesForGrade } from './trackOrderForGrade';
import { orderedTrackAliasesForGrade } from './trackTimetableAlias';
import { sanitizeWildcardChar, matchesTrackWildcardSuffix } from './trackWildcard';
import {
  loadTrackProgramModeMap,
  type TrackProgramMode,
} from './trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from './trackProgramSyncRepStorage';
import type { PageSectionLayout } from '../services/sectionLayoutService';
import type { TrackHoursTermScope } from './trackHoursTermScopeStorage';

export type AnnualTimetableHoursAdjustColumn = {
  trackName: string;
  /** 편제 프로그램 생성 방식 라벨 */
  kindLabel: string;
  programMode: TrackProgramMode;
  /** 1·2학기 구분 / 통합 */
  termScope: TrackHoursTermScope;
  /** 자동 조정 미포함 */
  excludeFromAdjust: boolean;
  /** 표시용 시간표명(여러 개면 쉼표 연결) */
  timetableNamesLabel: string;
  aliases: string[];
  /** 동기화 편제의 대표 시간표명 */
  syncRepAlias: string;
  criteriaHours: number;
  isOtherTrack: boolean;
  /** 고정 프로그램(행사) 색으로 채워진 시수 */
  fixedProgramHours: number;
  /** 고정이 아닌 프로그램으로 채워진 시수(실제 수업운영) */
  operatingHours: number;
};

function programModeLabel(mode: TrackProgramMode): string {
  if (mode === 'split') return '시간표별';
  if (mode === 'sync') return '동기화';
  return '편제별';
}

function countCellsForAliases(params: {
  byTextValue: Record<string, AnnualEffectiveCell[]>;
  aliases: string[];
  wildcardChar: string;
  mondayIsoSet: Set<string>;
  showSaturday: boolean;
  fixedOnly: boolean | null;
  regularColors: string[];
}): number {
  const { byTextValue, aliases, wildcardChar, mondayIsoSet, showSaturday, fixedOnly, regularColors } =
    params;
  const seen = new Set<string>();
  const addCell = (cell: AnnualEffectiveCell) => {
    if (!mondayIsoSet.has(cell.mondayIso)) return;
    if (!showSaturday && cell.weekdayKey === 'sat') return;
    if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return;
    const isFixed = isFixedProgramColor(cell.colorHex, regularColors);
    if (fixedOnly === true && !isFixed) return;
    if (fixedOnly === false && isFixed) return;
    seen.add(`${cell.mondayIso}|${cell.weekdayKey}|${cell.slotIndex}`);
  };

  const nameSet = new Set(aliases.map((a) => a.trim()).filter(Boolean));
  nameSet.forEach((name) => {
    (byTextValue[name] ?? []).forEach(addCell);
  });

  const wildcard = sanitizeWildcardChar(wildcardChar);
  if (wildcard) {
    Object.entries(byTextValue).forEach(([textValue, cells]) => {
      if (!matchesTrackWildcardSuffix(textValue, wildcard)) return;
      cells.forEach(addCell);
    });
  }
  return seen.size;
}

/**
 * 연간시간표 '시수 자동 조정' 모달용: 편제(열)별 기준·고정·실제운영 시수 (한 학기).
 * 페이지 메모리의 programGroup만 사용하며 네트워크 fetch는 하지 않는다.
 * 기타 편제는 제외하고 정규 편제만 반환한다.
 */
export function buildAnnualTimetableHoursAdjustColumns(params: {
  workspaceId: string;
  gradeId: string;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[] | null | undefined;
  programGroup: AnnualProgramGrouping | null | undefined;
  /** 통합 편제 집계용: 1·2학기 셀을 합친 그룹(없으면 programGroup만 사용) */
  combinedProgramGroup?: AnnualProgramGrouping | null | undefined;
  regularColors: string[];
  mondayIsos: string[];
  /** 통합 편제 시수 집계에 쓰는 전체 월요일 ISO */
  allMondayIsos?: string[];
  showSaturday: boolean;
  wildcardCharByTrackName?: Record<string, string>;
  termScopeByTrack?: Record<string, TrackHoursTermScope>;
  excludeFromAdjustByTrack?: Record<string, boolean>;
}): AnnualTimetableHoursAdjustColumn[] {
  const {
    workspaceId,
    gradeId,
    tracks,
    gradeTrackSections,
    programGroup,
    combinedProgramGroup,
    regularColors,
    mondayIsos,
    allMondayIsos,
    showSaturday,
    wildcardCharByTrackName = {},
    termScopeByTrack = {},
    excludeFromAdjustByTrack = {},
  } = params;

  const termMondayIsoSet = new Set(mondayIsos);
  const unifiedMondayIsoSet = new Set(allMondayIsos?.length ? allMondayIsos : mondayIsos);
  const termByText = programGroup?.byTextValue ?? {};
  const combinedByText = combinedProgramGroup?.byTextValue ?? termByText;
  const modeMap = loadTrackProgramModeMap(workspaceId, gradeId);
  const syncRepMap = loadTrackProgramSyncRepMap(workspaceId, gradeId);

  const orderedTrackNames = getOrderedTrackNamesForGrade(
    workspaceId,
    gradeId,
    tracks,
    gradeTrackSections
  );
  const trackOrderIndex = new Map(orderedTrackNames.map((name, i) => [name, i]));

  const rows = tracks.filter((t) => (t.grade_id ?? '') === gradeId);
  const map = new Map<
    string,
    {
      trackName: string;
      aliases: string[];
      criteriaHours: number;
      isOtherTrack: boolean;
    }
  >();

  rows.forEach((t) => {
    const trackName = (t.name ?? '').trim();
    if (!trackName) return;
    const alias = (t.description ?? '').trim();
    const h = Math.min(999, Math.max(0, Number(t.criteria_hours) || 0));
    if (!map.has(trackName)) {
      map.set(trackName, {
        trackName,
        aliases: [],
        criteriaHours: h,
        isOtherTrack: t.track_category === 'other',
      });
    } else if (t.track_category === 'other') {
      map.get(trackName)!.isOtherTrack = true;
    }
    if (alias) map.get(trackName)!.aliases.push(alias);
  });

  const groups = Array.from(map.values())
    .filter((g) => !g.isOtherTrack)
    .map((g) => {
      const aliasSet = Array.from(new Set(g.aliases)).filter(Boolean);
      const orderedAliases = orderedTrackAliasesForGrade(workspaceId, gradeId, g.trackName, tracks);
      return {
        ...g,
        aliases: orderedAliases.length > 0 ? orderedAliases : aliasSet,
      };
    })
    .sort((a, b) => {
      const orderA = trackOrderIndex.get(a.trackName) ?? 9999;
      const orderB = trackOrderIndex.get(b.trackName) ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      return a.trackName.localeCompare(b.trackName, 'ko');
    });

  return groups.map((g) => {
    const mode = modeMap[g.trackName] ?? 'unified';
    const termScope: TrackHoursTermScope = termScopeByTrack[g.trackName] ?? 'split';
    const storedRep = (syncRepMap[g.trackName] ?? '').trim();
    const syncRepAlias =
      storedRep && g.aliases.includes(storedRep) ? storedRep : (g.aliases[0] ?? '');
    let aliasesForCount = g.aliases;
    if (mode === 'sync' && g.aliases.length > 1) {
      aliasesForCount = syncRepAlias ? [syncRepAlias] : [];
    }

    const wildcardChar = wildcardCharByTrackName[g.trackName] ?? '';
    const useUnified = termScope === 'unified';
    const byTextValue = useUnified ? combinedByText : termByText;
    const mondayIsoSet = useUnified ? unifiedMondayIsoSet : termMondayIsoSet;

    const fixedProgramHours = countCellsForAliases({
      byTextValue,
      aliases: aliasesForCount,
      wildcardChar,
      mondayIsoSet,
      showSaturday,
      fixedOnly: true,
      regularColors,
    });
    const operatingHours = countCellsForAliases({
      byTextValue,
      aliases: aliasesForCount,
      wildcardChar,
      mondayIsoSet,
      showSaturday,
      fixedOnly: false,
      regularColors,
    });

    return {
      trackName: g.trackName,
      kindLabel: programModeLabel(mode),
      programMode: mode,
      termScope,
      excludeFromAdjust: excludeFromAdjustByTrack[g.trackName] === true,
      timetableNamesLabel: g.aliases.length > 0 ? g.aliases.join(', ') : '—',
      aliases: g.aliases,
      syncRepAlias,
      criteriaHours: g.criteriaHours,
      isOtherTrack: g.isOtherTrack,
      fixedProgramHours,
      operatingHours,
    };
  });
}
